/**
 * Achievement Checking Utility
 * Checks if user qualifies for achievements based on their stats
 */

import { ACHIEVEMENTS, Achievement } from '../data/achievements';
import { ReadingStats, SessionResult } from '../types';
import { UserProfileData } from '../types/membership';

export interface AchievementCheckResult {
  newlyUnlocked: Achievement[];
  totalXPFromAchievements: number;
}

/**
 * Check which achievements should be unlocked based on current stats
 */
export function checkAchievements(
  stats: ReadingStats,
  profile: UserProfileData | null,
  currentStreak: number = 0,
  sessionsCompleted: number = 0
): AchievementCheckResult {
  const newlyUnlocked: Achievement[] = [];
  let totalXPFromAchievements = 0;

  // Get already unlocked achievement IDs (would come from DB in real implementation)
  // For now, check all achievements against criteria

  for (const achievement of ACHIEVEMENTS) {
    // Skip if level requirement not met
    if (achievement.levelRequired && profile && profile.current_level < achievement.levelRequired) {
      continue;
    }

    const shouldUnlock = checkSingleAchievement(achievement, stats, profile, currentStreak, sessionsCompleted);
    
    if (shouldUnlock) {
      newlyUnlocked.push(achievement);
      totalXPFromAchievements += achievement.xpReward;
    }
  }

  return { newlyUnlocked, totalXPFromAchievements };
}

/**
 * Check if a single achievement should be unlocked
 */
function checkSingleAchievement(
  achievement: Achievement,
  stats: ReadingStats,
  profile: UserProfileData | null,
  currentStreak: number,
  sessionsCompleted: number
): boolean {
  switch (achievement.category) {
    case 'speed':
      return checkSpeedAchievements(achievement.id, stats);
    case 'volume':
      return checkVolumeAchievements(achievement.id, stats);
    case 'streak':
      return checkStreakAchievements(achievement.id, currentStreak, stats);
    case 'session':
      return checkSessionAchievements(achievement.id, sessionsCompleted, stats.history[stats.history.length - 1]);
    case 'milestone':
      return checkMilestoneAchievements(achievement.id, profile, stats);
    case 'challenge':
      return checkChallengeAchievements(achievement.id, stats);
    case 'variety':
      return checkVarietyAchievements(achievement.id, stats);
    case 'time':
      return checkTimeAchievements(achievement.id, stats);
    case 'secret':
      return checkSecretAchievements(achievement.id, stats);
    default:
      return false;
  }
}

// Speed achievement checks
function checkSpeedAchievements(achievementId: string, stats: ReadingStats): boolean {
  const { maxWpm, avgWpm, history, sessions } = stats;

  // Simple threshold achievements (based on maximum WPM ever reached)
  const speedThresholds: Record<string, number> = {
    'speed-100': 100,
    'speed-200': 200,
    'speed-300': 300,
    'speed-400': 400,
    'speed-500': 500,
    'speed-600': 600,
    'speed-700': 700,
    'speed-800': 800,
    'speed-900': 900,
    'speed-1000': 1000,
    'speed-1100': 1100,
    'speed-1200': 1200,
    'speed-1300': 1300,
    'speed-1400': 1400,
    'speed-1500': 1500,
    'speed-1600': 1600,
  };

  if (achievementId in speedThresholds) {
    return maxWpm >= speedThresholds[achievementId];
  }

  // Complex achievements requiring additional data
  switch (achievementId) {
    case 'speed-consistent-400': {
      // 10 sessions at 400+ WPM
      const consistentSessions = history.filter(s => s.avgWpm >= 400).length;
      return consistentSessions >= 10;
    }
    case 'speed-consistent-500': {
      // Count sessions where average WPM >= 500
      const consistentSessions = history.filter(s => s.avgWpm >= 500).length;
      return consistentSessions >= 10;
    }
    case 'speed-consistent-600': {
      // 10 sessions at 600+ WPM
      const consistentSessions = history.filter(s => s.avgWpm >= 600).length;
      return consistentSessions >= 10;
    }
    case 'speed-consistent-700': {
      // 5 sessions at 700+ WPM
      const consistentSessions = history.filter(s => s.avgWpm >= 700).length;
      return consistentSessions >= 5;
    }
    case 'speed-consistent-800': {
      // 5 sessions at 800+ WPM
      const consistentSessions = history.filter(s => s.avgWpm >= 800).length;
      return consistentSessions >= 5;
    }
    case 'speed-consistent-900': {
      // 5 sessions at 900+ WPM
      const consistentSessions = history.filter(s => s.avgWpm >= 900).length;
      return consistentSessions >= 5;
    }
    case 'speed-consistent-1000': {
      // 3 sessions at 1000+ WPM
      const consistentSessions = history.filter(s => s.avgWpm >= 1000).length;
      return consistentSessions >= 3;
    }
    case 'speed-consistent-1100': {
      // 3 sessions at 1100+ WPM
      const consistentSessions = history.filter(s => s.avgWpm >= 1100).length;
      return consistentSessions >= 3;
    }
    case 'speed-consistent-1200': {
      // 3 sessions at 1200+ WPM
      const consistentSessions = history.filter(s => s.avgWpm >= 1200).length;
      return consistentSessions >= 3;
    }
    case 'speed-average-400': {
      // Average 400+ WPM over 50 sessions
      return avgWpm >= 400 && sessions >= 50;
    }
    case 'speed-improvement-100': {
      // Improve WPM by 100 from starting (first session's max)
      if (history.length < 2) return false;
      const startingMax = history[0].maxWpm;
      return (maxWpm - startingMax) >= 100;
    }
    case 'speed-improvement-200': {
      // Double starting WPM
      if (history.length < 2) return false;
      const startingMax = history[0].maxWpm;
      if (startingMax <= 0) return false; // avoid division by zero
      return maxWpm >= 2 * startingMax;
    }
    case 'speed-max-record': {
      // New personal best set in the latest session
      if (history.length < 2) return false;
      const latest = history[history.length - 1];
      const previousSessions = history.slice(0, -1);
      const previousMax = Math.max(...previousSessions.map(s => s.maxWpm));
      return latest.maxWpm > previousMax;
    }
    default:
      return false;
  }
}

// Volume achievement checks
function checkVolumeAchievements(achievementId: string, stats: ReadingStats): boolean {
  const volumeThresholds: Record<string, number> = {
    'volume-1000': 1000,
    'volume-10000': 10000,
    'volume-50000': 50000,
    'volume-100000': 100000,
    'volume-250000': 250000,
    'volume-500000': 500000,
    'volume-million': 1000000,
    'volume-2million': 2000000,
    'volume-5million': 5000000,
    'volume-10million': 10000000,
    'volume-15million': 15000000,
    'volume-20million': 20000000,
  };
  if (achievementId in volumeThresholds) {
    return stats.wordsRead >= volumeThresholds[achievementId];
  }

  // Handle book-based volumes
  if (achievementId.startsWith('volume-book-') || achievementId === 'volume-books-1000') {
    const bookTargets: Record<string, number> = {
      'volume-book-1': 1,
      'volume-book-5': 5,
      'volume-book-25': 25,
      'volume-book-100': 100,
      'volume-book-500': 500,
      'volume-books-1000': 1000,
    };
    if (achievementId in bookTargets) {
      return stats.booksCompleted >= bookTargets[achievementId];
    }
    return false;
  }

  // Handle daily volume
  if (achievementId === 'volume-day-100k') {
    return stats.peakDailyWords >= 100000;
  }

  return false;
}

// Streak achievement checks
function checkStreakAchievements(achievementId: string, currentStreak: number, stats: ReadingStats): boolean {
  // Basic streak length thresholds
  const streakThresholds: Record<string, number> = {
    'streak-3': 3,
    'streak-7': 7,
    'streak-14': 14,
    'streak-21': 21,
    'streak-30': 30,
    'streak-60': 60,
    'streak-100': 100,
    'streak-180': 180,
    'streak-365': 365,
    'streak-250': 250,
    'streak-400': 400,
    'streak-500': 500,
  };
  if (achievementId in streakThresholds) {
    return currentStreak >= streakThresholds[achievementId];
  }

  // Advanced streak checks using stats fields
  switch (achievementId) {
    case 'streak-multiplier-2x':
      return (stats.streakMultiplier || 1) >= 2;
    case 'streak-multiplier-3x':
      return (stats.streakMultiplier || 1) >= 3;
    case 'streak-multiplier-5x':
      return (stats.streakMultiplier || 1) >= 5;
    case 'streak-multiplier-10x':
      return (stats.streakMultiplier || 1) >= 10;
    case 'streak-recovery':
      return stats.streakRecovery === true;
    case 'streak-bounce-back':
      return stats.streakBounceBack === true;
    case 'streak-longest':
      return stats.newRecord === true;
    default:
      return false;
  }
}

// Session achievement checks
function checkSessionAchievements(achievementId: string, sessions: number, sessionResult?: SessionResult): boolean {
  const sessionThresholds: Record<string, number> = {
    'session-1': 1,
    'session-10': 10,
    'session-50': 50,
    'session-100': 100,
    'session-500': 500,
    'session-1000': 1000,
    'session-5000': 5000,
  };
  if (achievementId in sessionThresholds) {
    return sessions >= sessionThresholds[achievementId];
  }

  switch (achievementId) {
    case 'session-long-30':
      return sessionResult ? sessionResult.durationSeconds >= 30 * 60 : false;
    case 'session-long-60':
      return sessionResult ? sessionResult.durationSeconds >= 60 * 60 : false;
    case 'session-no-pause':
      return sessionResult ? sessionResult.paused === false : false;
    default:
      return false;
  }
}

// Milestone achievement checks
function checkMilestoneAchievements(achievementId: string, profile: UserProfileData | null, stats: ReadingStats): boolean {
  if (!profile) return false;

  switch (achievementId) {
    case 'milestone-level-5':
      return profile.current_level >= 5;
    case 'milestone-level-10':
      return profile.current_level >= 10;
    case 'milestone-xp-10k':
      return profile.total_xp >= 10000;
    case 'milestone-xp-100k':
      return profile.total_xp >= 100000;
    case 'milestone-xp-500k':
      return profile.total_xp >= 500000;
    case 'milestone-xp-1M':
      return profile.total_xp >= 1000000;
    case 'milestone-achievements-50':
      return (stats.achievementsUnlocked || 0) >= 50;
    default:
      return false;
  }
}

// Challenge achievement checks
function checkChallengeAchievements(achievementId: string, stats: ReadingStats): boolean {
  // Challenge completions (total count)
  const completionThresholds: Record<string, number> = {
    'challenge-1': 1,
    'challenge-10': 10,
    'challenge-50': 50,
    'challenge-100': 100,
    'challenge-500': 500,
    'challenge-2000': 2000,
  };
  if (achievementId in completionThresholds) {
    return stats.challengesCompleted >= completionThresholds[achievementId];
  }

  // Perfection days (days where all 3 daily challenges were completed)
  const perfectionThresholds: Record<string, number> = {
    'challenge-perfection': 1,   // original: at least 1 perfect day
    'challenge-perfection-10': 10,
    'challenge-perfection-50': 50,
  };
  if (achievementId in perfectionThresholds) {
    return stats.perfectDaysCount >= perfectionThresholds[achievementId];
  }

  // Specific challenge type achievements (check against session history or stats)
  switch (achievementId) {
    case 'challenge-speed-500': {
      // Check if any session had avgWpm >= 500
      // (challenge itself requires 500 WPM in a session)
      return stats.history.some(s => s.avgWpm >= 500);
    }
    case 'challenge-volume-10k': {
      // Check if any single day had 10k+ words (peakDailyWords tracks this)
      return stats.peakDailyWords >= 10000;
    }
    case 'challenge-marathon': {
      // Check if any session lasted >= 60 minutes (3600 seconds)
      return stats.history.some(s => s.durationSeconds >= 3600);
    }
    case 'challenge-ultra-marathon': {
      // Check if any session lasted >= 120 minutes (7200 seconds)
      return stats.history.some(s => s.durationSeconds >= 7200);
    }
    default:
      return false;
  }
}

// Variety achievement checks
function checkVarietyAchievements(achievementId: string, stats: ReadingStats): boolean {
  // Genre count achievements
  const genreThresholds: Record<string, number> = {
    'variety-genres-3': 3,
    'variety-genres-10': 10,
    'variety-genres-12': 12,
    'variety-genres-25': 25,
  };
  if (achievementId in genreThresholds) {
    return (stats.genresRead?.length || 0) >= genreThresholds[achievementId];
  }

  // Source count achievements
  const sourceThresholds: Record<string, number> = {
    'variety-sources-5': 5,
    'variety-sources-20': 20,
    'variety-sources-50': 50,
    'variety-sources-100': 100,
  };
  if (achievementId in sourceThresholds) {
    return (stats.sourcesRead?.length || 0) >= sourceThresholds[achievementId];
  }

  // Format count achievements
  const formatThresholds: Record<string, number> = {
    'variety-format-3': 3,
    'variety-formats-5': 5,
  };
  if (achievementId in formatThresholds) {
    return (stats.formatsRead?.length || 0) >= formatThresholds[achievementId];
  }

  // Time of day achievements
  const { timeOfDaySessions } = stats;
  const hasMorning = (timeOfDaySessions?.morning || 0) > 0;
  const hasAfternoon = (timeOfDaySessions?.afternoon || 0) > 0;
  const hasEvening = (timeOfDaySessions?.evening || 0) > 0;
  const hasNight = (timeOfDaySessions?.night || 0) > 0;

  switch (achievementId) {
    case 'variety-time-morning':
      // Read during all four time periods
      return hasMorning && hasAfternoon && hasEvening && hasNight;
    case 'variety-time-evening':
      // Read during any three time periods
      const countThree = [hasMorning, hasAfternoon, hasEvening, hasNight].filter(Boolean).length;
      return countThree >= 3;
    case 'variety-time-night':
      // Read during the night (10 PM - 5 AM)
      return hasNight;
    case 'variety-time-weekend': {
      // Read on both Saturday and Sunday
      const days = new Set<number>();
      for (const session of stats.history || []) {
        const date = new Date(session.date);
        const day = date.getDay(); // 0 = Sunday, 6 = Saturday
        days.add(day);
        if (days.has(0) && days.has(6)) {
          return true;
        }
      }
      return false;
    }
    default:
      return false;
  }
}

// Time achievement checks
function checkTimeAchievements(achievementId: string, stats: ReadingStats): boolean {
  const hoursRead = stats.timeSpentSeconds / 3600;
  const timeThresholds: Record<string, number> = {
    'time-1hour': 1,
    'time-10hours': 10,
    'time-50hours': 50,
    'time-100hours': 100,
    'time-500hours': 500,
    'time-1000hours': 1000,
  };
  if (achievementId in timeThresholds) {
    return hoursRead >= timeThresholds[achievementId];
  }

  // Daily, weekly, monthly time achievements
  switch (achievementId) {
    case 'time-daily-1hour':
      return (stats.todaySeconds || 0) >= 60 * 60;
    case 'time-daily-2hours':
      return (stats.todaySeconds || 0) >= 2 * 60 * 60;
    case 'time-daily-4hours':
      return (stats.todaySeconds || 0) >= 4 * 60 * 60;
    case 'time-daily-6hours':
      return (stats.todaySeconds || 0) >= 6 * 60 * 60;
    case 'time-week-20hours':
      return (stats.weekSeconds || 0) >= 20 * 60 * 60;
    case 'time-week-30hours':
      return (stats.weekSeconds || 0) >= 30 * 60 * 60;
    case 'time-month-100hours':
      return (stats.monthSeconds || 0) >= 100 * 60 * 60;
    default:
      return false;
  }
}

// Secret achievement checks
function checkSecretAchievements(achievementId: string, stats: ReadingStats): boolean {
  if (!stats.history || stats.history.length === 0) return false;

  switch (achievementId) {
    case 'secret-midnight': {
      // Read at exactly midnight (00:00)
      return stats.history.some(session => {
        const date = new Date(session.date);
        return date.getHours() === 0 && date.getMinutes() === 0;
      });
    }
    case 'secret-palindrome': {
      // Read at a palindromic time (HH:MM reads same forwards/backwards)
      // Examples: 12:21, 02:20, 03:30, 11:11, etc.
      return stats.history.some(session => {
        const date = new Date(session.date);
        const hour = date.getHours();
        const minute = date.getMinutes();
        const timeStr = `${hour.toString().padStart(2, '0')}${minute.toString().padStart(2, '0')}`;
        return timeStr === timeStr.split('').reverse().join('');
      });
    }
    case 'secret-420': {
      // Read at exactly 420 WPM (rounded to nearest integer)
      return stats.history.some(session => Math.round(session.avgWpm) === 420);
    }
    case 'secret-first-of-month': {
      // Read on the 1st of any month
      return stats.history.some(session => {
        const date = new Date(session.date);
        return date.getDate() === 1;
      });
    }
    case 'secret-leap-year': {
      // Read on February 29th
      return stats.history.some(session => {
        const date = new Date(session.date);
        return date.getMonth() === 1 && date.getDate() === 29; // month 1 = February
      });
    }
    case 'secret-christmas': {
      // Read on December 25th
      return stats.history.some(session => {
        const date = new Date(session.date);
        return date.getMonth() === 11 && date.getDate() === 25; // month 11 = December
      });
    }
    case 'secret-friday-13': {
      // Read on Friday the 13th (Friday = day 5 in JavaScript getDay())
      return stats.history.some(session => {
        const date = new Date(session.date);
        return date.getDay() === 5 && date.getDate() === 13;
      });
    }
    case 'secret-1111': {
      // Read at exactly 11:11 AM (11:11) or PM (23:11)
      return stats.history.some(session => {
        const date = new Date(session.date);
        const hour = date.getHours();
        const minute = date.getMinutes();
        return (hour === 11 && minute === 11) || (hour === 23 && minute === 11);
      });
    }
    case 'secret-pi': {
      // Read at exactly 3:14 or 15:14
      return stats.history.some(session => {
        const date = new Date(session.date);
        const hour = date.getHours();
        const minute = date.getMinutes();
        return (hour === 3 && minute === 14) || (hour === 15 && minute === 14);
      });
    }
    case 'secret-666': {
      // Read at exactly 666 WPM (rounded to nearest integer)
      return stats.history.some(session => Math.round(session.avgWpm) === 666);
    }
    default:
      return false;
  }
}

/**
 * Get achievement progress for display
 */
export function getAchievementProgress(achievement: Achievement, stats: ReadingStats, profile: UserProfileData | null): number {
  // Return 0-100 percentage for progress display
  // This would need implementation based on achievement type
  
  switch (achievement.category) {
    case 'speed': {
      const { maxWpm, avgWpm, history, sessions } = stats;
      const speedThresholds: Record<string, number> = {
        'speed-100': 100, 'speed-200': 200, 'speed-300': 300, 'speed-400': 400,
        'speed-500': 500, 'speed-600': 600, 'speed-700': 700, 'speed-800': 800,
        'speed-900': 900, 'speed-1000': 1000,
        'speed-1100': 1100, 'speed-1200': 1200, 'speed-1300': 1300,
        'speed-1400': 1400, 'speed-1500': 1500, 'speed-1600': 1600,
      };
      if (achievement.id in speedThresholds) {
        const threshold = speedThresholds[achievement.id];
        return threshold > 0 ? Math.min((maxWpm / threshold) * 100, 100) : 0;
      }

      switch (achievement.id) {
        case 'speed-consistent-400': {
          const consistentSessions = history.filter(s => s.avgWpm >= 400).length;
          return Math.min((consistentSessions / 10) * 100, 100);
        }
        case 'speed-consistent-500': {
          const consistentSessions = history.filter(s => s.avgWpm >= 500).length;
          return Math.min((consistentSessions / 10) * 100, 100);
        }
        case 'speed-consistent-600': {
          const consistentSessions = history.filter(s => s.avgWpm >= 600).length;
          return Math.min((consistentSessions / 10) * 100, 100);
        }
        case 'speed-consistent-700': {
          const consistentSessions = history.filter(s => s.avgWpm >= 700).length;
          return Math.min((consistentSessions / 5) * 100, 100);
        }
        case 'speed-consistent-800': {
          const consistentSessions = history.filter(s => s.avgWpm >= 800).length;
          return Math.min((consistentSessions / 5) * 100, 100);
        }
        case 'speed-consistent-900': {
          const consistentSessions = history.filter(s => s.avgWpm >= 900).length;
          return Math.min((consistentSessions / 5) * 100, 100);
        }
        case 'speed-consistent-1000': {
          const consistentSessions = history.filter(s => s.avgWpm >= 1000).length;
          return Math.min((consistentSessions / 3) * 100, 100);
        }
        case 'speed-consistent-1100': {
          const consistentSessions = history.filter(s => s.avgWpm >= 1100).length;
          return Math.min((consistentSessions / 3) * 100, 100);
        }
        case 'speed-consistent-1200': {
          const consistentSessions = history.filter(s => s.avgWpm >= 1200).length;
          return Math.min((consistentSessions / 3) * 100, 100);
        }
        case 'speed-average-400': {
          const sessionProgress = Math.min((sessions / 50) * 100, 100);
          const speedProgress = Math.min((avgWpm / 400) * 100, 100);
          return Math.min(sessionProgress, speedProgress);
        }
        case 'speed-improvement-100': {
          if (history.length < 2) return 0;
          const startingMax = history[0].maxWpm;
          const improvement = maxWpm - startingMax;
          return Math.min((improvement / 100) * 100, 100);
        }
        case 'speed-improvement-200': {
          if (history.length < 2) return 0;
          const startingMax = history[0].maxWpm;
          if (startingMax <= 0) return 0;
          const ratio = maxWpm / (2 * startingMax);
          return Math.min(ratio * 100, 100);
        }
        case 'speed-max-record':
          // No progress while locked
          return 0;
        default:
          return 0;
      }
    }
    case 'volume': {
      const bookTargets: Record<string, number> = {
        'volume-book-1': 1, 'volume-book-5': 5, 'volume-book-25': 25,
        'volume-book-100': 100, 'volume-book-500': 500, 'volume-books-1000': 1000,
      };
      if (achievement.id in bookTargets) {
        const threshold = bookTargets[achievement.id];
        return Math.min((stats.booksCompleted / threshold) * 100, 100);
      }
      if (achievement.id === 'volume-day-100k') {
        return Math.min((stats.peakDailyWords / 100000) * 100, 100);
      }
      const wordThresholds: Record<string, number> = {
        'volume-1000': 1000, 'volume-10000': 10000, 'volume-50000': 50000,
        'volume-100000': 100000, 'volume-250000': 250000, 'volume-500000': 500000,
        'volume-million': 1000000, 'volume-2million': 2000000,
        'volume-5million': 5000000, 'volume-10million': 10000000,
        'volume-15million': 15000000, 'volume-20million': 20000000,
      };
      const threshold = wordThresholds[achievement.id] || 0;
      return threshold > 0 ? Math.min((stats.wordsRead / threshold) * 100, 100) : 0;
    }
    case 'streak': {
      if (!profile) return 0;
      const streakThresholds: Record<string, number> = {
        'streak-3': 3, 'streak-7': 7, 'streak-14': 14, 'streak-21': 21,
        'streak-30': 30, 'streak-60': 60, 'streak-100': 100, 'streak-180': 180,
        'streak-365': 365, 'streak-250': 250, 'streak-400': 400, 'streak-500': 500,
      };
      const threshold = streakThresholds[achievement.id] || 0;
      if (threshold > 0) {
        return Math.min((profile.current_streak / threshold) * 100, 100);
      }
      switch (achievement.id) {
        case 'streak-multiplier-2x':
        case 'streak-multiplier-3x':
        case 'streak-multiplier-5x':
        case 'streak-multiplier-10x': {
          const multiplier = stats.streakMultiplier || 1;
          // For multiplier achievements, progress based on how close to target multiplier
          const target = parseInt(achievement.id.split('-')[2].replace('x', ''), 10);
          return Math.min((multiplier / target) * 100, 100);
        }
        case 'streak-recovery':
        case 'streak-bounce-back':
        case 'streak-longest':
          // These are one-time flags; no progress while locked
          return 0;
        default:
          return 0;
      }
    }
    case 'session': {
      const sessionThresholds: Record<string, number> = {
        'session-1': 1, 'session-10': 10, 'session-50': 50, 'session-100': 100,
        'session-500': 500, 'session-1000': 1000, 'session-5000': 5000,
      };
      if (achievement.id in sessionThresholds) {
        const threshold = sessionThresholds[achievement.id];
        return Math.min((stats.sessions / threshold) * 100, 100);
      }
      switch (achievement.id) {
        case 'session-long-30': {
          const latest = stats.history[stats.history.length - 1];
          if (!latest) return 0;
          return Math.min((latest.durationSeconds / (30 * 60)) * 100, 100);
        }
        case 'session-long-60': {
          const latest = stats.history[stats.history.length - 1];
          if (!latest) return 0;
          return Math.min((latest.durationSeconds / (60 * 60)) * 100, 100);
        }
        case 'session-no-pause': {
          const latest = stats.history[stats.history.length - 1];
          return latest && !latest.paused ? 100 : 0;
        }
        default:
          return 0;
      }
    }
    case 'milestone': {
      if (!profile) return 0;
      switch (achievement.id) {
        case 'milestone-level-5':
          return Math.min((profile.current_level / 5) * 100, 100);
        case 'milestone-level-10':
          return Math.min((profile.current_level / 10) * 100, 100);
        case 'milestone-xp-10k':
          return Math.min((profile.total_xp / 10000) * 100, 100);
        case 'milestone-xp-100k':
          return Math.min((profile.total_xp / 100000) * 100, 100);
        case 'milestone-xp-500k':
          return Math.min((profile.total_xp / 500000) * 100, 100);
        case 'milestone-xp-1M':
          return Math.min((profile.total_xp / 1000000) * 100, 100);
        case 'milestone-achievements-50':
          return Math.min(((stats.achievementsUnlocked || 0) / 50) * 100, 100);
        default:
          return 0;
      }
    }
    case 'time': {
      const hoursRead = stats.timeSpentSeconds / 3600;
      const timeThresholds: Record<string, number> = {
        'time-1hour': 1, 'time-10hours': 10, 'time-50hours': 50,
        'time-100hours': 100, 'time-500hours': 500, 'time-1000hours': 1000,
      };
      if (achievement.id in timeThresholds) {
        const threshold = timeThresholds[achievement.id];
        return threshold > 0 ? Math.min((hoursRead / threshold) * 100, 100) : 0;
      }
      switch (achievement.id) {
        case 'time-daily-1hour':
        case 'time-daily-2hours':
        case 'time-daily-4hours':
        case 'time-daily-6hours': {
          const todaySec = stats.todaySeconds || 0;
          const threshold = achievement.id === 'time-daily-1hour' ? 3600 :
                            achievement.id === 'time-daily-2hours' ? 7200 :
                            achievement.id === 'time-daily-4hours' ? 14400 : 21600;
          return Math.min((todaySec / threshold) * 100, 100);
        }
        case 'time-week-20hours':
        case 'time-week-30hours': {
          const weekSec = stats.weekSeconds || 0;
          const threshold = achievement.id === 'time-week-20hours' ? 72000 : 108000; // 20h=72000, 30h=108000
          return Math.min((weekSec / threshold) * 100, 100);
        }
        case 'time-month-100hours': {
          const monthSec = stats.monthSeconds || 0;
          const threshold = 360000; // 100h = 360000 seconds
          return Math.min((monthSec / threshold) * 100, 100);
        }
        default:
          return 0;
      }
    }
    case 'challenge': {
      // Completion count achievements
      const completionThresholds: Record<string, number> = {
        'challenge-1': 1, 'challenge-10': 10, 'challenge-50': 50,
        'challenge-100': 100, 'challenge-500': 500, 'challenge-2000': 2000,
      };
      if (achievement.id in completionThresholds) {
        const threshold = completionThresholds[achievement.id];
        return Math.min((stats.challengesCompleted / threshold) * 100, 100);
      }

      // Perfection day achievements
      const perfectionThresholds: Record<string, number> = {
        'challenge-perfection': 1,
        'challenge-perfection-10': 10,
        'challenge-perfection-50': 50,
      };
      if (achievement.id in perfectionThresholds) {
        const threshold = perfectionThresholds[achievement.id];
        return Math.min((stats.perfectDaysCount / threshold) * 100, 100);
      }

      // Specific challenge achievements: no progress while locked (binary)
      switch (achievement.id) {
        case 'challenge-speed-500':
        case 'challenge-volume-10k':
        case 'challenge-marathon':
        case 'challenge-ultra-marathon':
          // These are one-time checks based on past sessions
          return 0;
        default:
          return 0;
        }
      }
      case 'secret': {
        // WPM-based secret achievements show progress based on maxWpm
        const wpmThresholds: Record<string, number> = {
          'secret-420': 420,
          'secret-666': 666,
        };
        if (achievement.id in wpmThresholds) {
          const threshold = wpmThresholds[achievement.id];
          return Math.min((stats.maxWpm / threshold) * 100, 100);
        }
        // Date/time-based secrets are binary: 0% until achieved
        return 0;
      }
      case 'variety': {
      // Genre count progress
      const genreThresholds: Record<string, number> = {
        'variety-genres-3': 3,
        'variety-genres-10': 10,
        'variety-genres-12': 12,
        'variety-genres-25': 25,
      };
      if (achievement.id in genreThresholds) {
        const threshold = genreThresholds[achievement.id];
        return Math.min(((stats.genresRead?.length || 0) / threshold) * 100, 100);
      }

      // Source count progress
      const sourceThresholds: Record<string, number> = {
        'variety-sources-5': 5,
        'variety-sources-20': 20,
        'variety-sources-50': 50,
        'variety-sources-100': 100,
      };
      if (achievement.id in sourceThresholds) {
        const threshold = sourceThresholds[achievement.id];
        return Math.min(((stats.sourcesRead?.length || 0) / threshold) * 100, 100);
      }

      // Format count progress
      const formatThresholds: Record<string, number> = {
        'variety-format-3': 3,
        'variety-formats-5': 5,
      };
      if (achievement.id in formatThresholds) {
        const threshold = formatThresholds[achievement.id];
        return Math.min(((stats.formatsRead?.length || 0) / threshold) * 100, 100);
      }

      // Time of day progress
      const { timeOfDaySessions } = stats;
      const hasMorning = (timeOfDaySessions?.morning || 0) > 0;
      const hasAfternoon = (timeOfDaySessions?.afternoon || 0) > 0;
      const hasEvening = (timeOfDaySessions?.evening || 0) > 0;
      const hasNight = (timeOfDaySessions?.night || 0) > 0;

      switch (achievement.id) {
        case 'variety-time-morning': {
          const count = [hasMorning, hasAfternoon, hasEvening, hasNight].filter(Boolean).length;
          return (count / 4) * 100;
        }
        case 'variety-time-evening': {
          const count = [hasMorning, hasAfternoon, hasEvening, hasNight].filter(Boolean).length;
          return Math.min((count / 3) * 100, 100);
        }
        case 'variety-time-night':
          return hasNight ? 100 : 0;
        case 'variety-time-weekend': {
          const days = new Set<number>();
          for (const session of stats.history || []) {
            const day = new Date(session.date).getDay();
            days.add(day);
          }
          return (days.has(0) && days.has(6)) ? 100 : 0;
        }
        default:
          return 0;
      }
    }
    default:
      return 0;
  }
}